import classes from 'classnames';
import { memo, useCallback, useEffect, useRef } from 'react';
import MinusIcon from 'assets/icons/minus.svg?react';
import PlusIcon from 'assets/icons/plus.svg?react';
import ErrorMessage from 'components/ErrorMessage/ErrorMessage';
import styles from './Stepper.module.scss';
import { EventCollectorProps } from 'hooks/context/EventCollector/types';
import useDebounce from 'hooks/useDebounce';
import useEventCollector from 'hooks/context/EventCollector/useEventCollector';
import TextInputEvent from 'hooks/context/EventCollector/events/TextInputEvent';

export type Props = {
  name?: string;
  value?: number;
  max?: number;
  min?: number;
  step?: number;
  disabled?: boolean;
  dataTest?: string;
  error?: string;
  onChange?: (name: string, value: number) => void;
  isTouched?: boolean;
};

function Stepper({
  name = 'default',
  value = 0,
  disabled = false,
  dataTest = 'stepper',
  onChange,
  min = 0,
  max = 99,
  step = 1,
  error,
  eventCollector,
  isTouched,
}: Props & EventCollectorProps) {
  const valueRef = useRef(value);
  const { track } = useEventCollector();
  const handleEventCollector = useCallback(
    (val: number) => {
      if (eventCollector?.dontTrack || valueRef.current === val) {
        return;
      }
      valueRef.current = value;
      track(
        new TextInputEvent({
          value: eventCollector?.dontTrackContent
            ? '<not tracked>'
            : (eventCollector?.contentOverride ?? val),
          description: eventCollector?.description ?? name ?? '',
        }),
      );

      valueRef.current = val;
    },
    [
      eventCollector?.contentOverride,
      eventCollector?.description,
      eventCollector?.dontTrack,
      eventCollector?.dontTrackContent,
      name,
      track,
      value,
    ],
  );

  useDebounce(() => handleEventCollector(value), 1000, [value]);

  useEffect(() => {
    if (onChange) {
      if (value > max) {
        onChange(name, max);
      } else if (value < min) {
        onChange(name, min);
      }
    }
  }, [value, onChange, min, max, name]);

  const decrease = useCallback(() => {
    onChange?.(name, value - step);
  }, [value, disabled, min, step, name, onChange]);

  const increase = useCallback(() => {
    onChange?.(name, value + step);
  }, [value, disabled, max, step, name, onChange]);

  const isMinDisabled = disabled || value === min;
  const isMaxDisabled = disabled || value === max;

  return (
    <>
      <div className="bg-background border border-b-0 rounded-tl-sm rounded-tr-sm flex h-12 w-36">
        <button
          type="button"
          onClick={decrease}
          data-test={`decrease-${dataTest}`}
          className={classes(
            styles['step-box'],
            isMinDisabled ? styles.disabled : styles.active,
          )}
          disabled={isMinDisabled}
        >
          <span className="sr-only">Přidat</span>
          <MinusIcon
            className="fill-primary"
            data-test={`${dataTest}-minus-icon`}
          />
        </button>
        <input
          id={name}
          name={name}
          disabled={disabled}
          className="w-12 border-b-4 border-l border-r text-center flex justify-center"
          value={value}
          data-test={dataTest}
          readOnly
        />
        <button
          type="button"
          onClick={increase}
          data-test={`increase-${dataTest}`}
          className={classes(
            styles['step-box'],
            isMaxDisabled ? styles.disabled : styles.active,
          )}
          disabled={isMaxDisabled}
        >
          <span className="sr-only">Odebrat</span>
          <PlusIcon
            className="fill-primary"
            data-test={`${dataTest}-plus-icon`}
          />
        </button>
      </div>
      {isTouched && Boolean(error) && (
        <div className={styles['error-message']}>
          <ErrorMessage errors={error} dataTest={`${dataTest}-errorMessage`} />
        </div>
      )}
    </>
  );
}

const StepperMemo = memo(Stepper);

export default StepperMemo;
